import { Component, inject, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import notify from 'devextreme/ui/notify';
import {
    DxButtonComponent,
    DxDataGridComponent,
    DxFormComponent,
    DxTemplateDirective,
} from 'devextreme-angular';
import {
    DxiButtonComponent,
    DxiColumnComponent,
    DxiItemComponent,
    DxoColumnChooserComponent,
    DxoExportComponent,
    DxoFilterRowComponent,
    DxoHeaderFilterComponent,
    DxoLoadPanelComponent,
    DxoPagerComponent,
    DxoPagingComponent,
    DxoPositionComponent,
    DxoScrollingComponent,
    DxoSearchComponent,
    DxoSelectionComponent,
    DxoSortingComponent,
    DxoToolbarComponent,
} from 'devextreme-angular/ui/nested';
import { AccessType, AuthService, CreateVendorCommand, InvoicingClient, PermissionType, UpdateVendorCommand, VendorGridViewDto, VendorStatus, VendorTerm } from '@services';
import { NgClass } from '@angular/common';
import { DxDataGridTypes } from 'devextreme-angular/ui/data-grid';
import EnumUtils from '../../../shared/enum-utils';
import { FilterPanelComponent } from '../../../components/library/filter-panel/filter-panel.component';
import { PhoneFormatPipe } from 'src/app/pipes/phone-format.pipe';
import { CapitalCasePipe } from '../../../pipes/capital-case.pipe';
import {
    FilterConfig,
    GridPageComponent,
} from '../../common/grid-page.component';
import { DATE_FORMAT, DATE_TIME_FORMAT } from 'src/app/shared/constants';
import { UserAvatarEmailComponent } from 'src/app/components/utils/user-avatar-email/user-avatar-email.component';
import { VendorTermPipe } from 'src/app/pipes/vendor-term.pipe';
import { VendorFormComponent } from './vendor-form/vendor-form.component';

@Component({
    selector: 'app-vendors',
    imports: [
        CommonModule,
        CapitalCasePipe,
        DxDataGridComponent,
        DxiColumnComponent,
        DxoHeaderFilterComponent,
        DxoScrollingComponent,
        DxButtonComponent,
        DxTemplateDirective,
        DxiItemComponent,
        DxoColumnChooserComponent,
        DxoExportComponent,
        DxoLoadPanelComponent,
        DxoSortingComponent,
        DxoToolbarComponent,
        DxoPagingComponent,
        DxoPagerComponent,
        DxoFilterRowComponent,
        DxFormComponent,
        FilterPanelComponent,
        DxiButtonComponent,
        DxoPositionComponent,
        DxoSearchComponent,
        DxoSelectionComponent,
        PhoneFormatPipe,
        NgClass,
        UserAvatarEmailComponent,
        VendorFormComponent,
    ],
    providers: [VendorTermPipe],
    templateUrl: './vendors.component.html',
})
export class VendorsComponent extends GridPageComponent<VendorGridViewDto> {
    @ViewChild(VendorFormComponent, { static: false }) vendorForm: VendorFormComponent;

    private authService = inject(AuthService);

    private invoicingClient = inject(InvoicingClient);
    private vendorTermPipe = inject(VendorTermPipe);

    protected override exportName: string = 'Vendors';
    protected offset: number = 100;
    public dateTimeFormat = DATE_TIME_FORMAT;
    public dateFormat = DATE_FORMAT;

    isVendorPopupOpened = false;
    vendorId: string | null = null;

    dataSource = this.dataSources.vendors();

    vendorStatusOptions = EnumUtils.getEnumOptions(VendorStatus);
    vendorTermOptions = EnumUtils.getEnumOptions(VendorTerm, (v) =>
        this.vendorTermPipe.transform(v),
    );

    lastModifiedByFilter = this.userNameOrEmailFilter('lastModifiedBy');
    createdByFilter = this.userNameOrEmailFilter('createdBy');

    protected override filterConfig: FilterConfig[] = [
        { field: 'number', op: 'contains' },
        { field: 'name', op: 'contains' },
        { field: 'serviceTypeName', op: 'contains' },
        { field: 'address', op: 'contains' },
        { field: 'phone', op: 'contains' },
        { field: 'email', op: 'contains' },
        { field: 'status', op: '=' },
        { field: 'term', op: '=' },
        { field: 'contractExpirationDate', op: '>=' },
        { field: 'lastModifiedBy.name', op: 'contains', customFilter: this.lastModifiedByFilter},
        { field: 'lastModified', op: '>=' },
        { field: 'createdBy.name', op: 'contains', customFilter: this.createdByFilter},
        { field: 'created', op: '>=' },
    ];

    constructor() {
        super();
        this.init(VendorGridViewDto);
    }

    protected getBadgeClass(status: VendorStatus): string {
        switch (status) {
            case VendorStatus.Inactive:
                return 'bg-secondary-subtle text-secondary';
            case VendorStatus.Active:
                return 'bg-success-subtle text-success';
            default:
                return 'bg-info-subtle text-info';
        }
    }

    addVendor() {
        this.vendorId = null;
        this.isVendorPopupOpened = true;
    }

    override onRowDblClick(e: DxDataGridTypes.RowDblClickEvent) {
        const { data } = e;

        this.vendorId = data.id;
        this.isVendorPopupOpened = true;
    }

    protected async onSave() {
        const vendorData = this.vendorForm.getData();
        this.isVendorPopupOpened = false;

        const command = this.vendorId ?
                new UpdateVendorCommand({ ...vendorData })
            : new CreateVendorCommand({ ...vendorData });

        const request$ = this.vendorId
            ? this.invoicingClient.updateVendor(command)
            : this.invoicingClient.createVendor(command);

         request$.subscribe({
            next: async () => {
                    notify(
                    this.vendorId
                        ? 'Vendor updated successfully'
                        : 'Vendor created successfully',
                    'success',
                    3000,
                );

                await this.dataGrid.instance.refresh();
            },
           error: (errors: Array<any>) => {
                const msg = errors?.[0]?.message||
                    (this.vendorId
                        ? 'Failed to update vendor'
                        : 'Failed to create vendor');
                notify(msg, 'error', 3000);
            },
        });
    }

    protected onDelete = ($event: any) => {
        this.dataGrid.instance.deleteRow($event.row.dataIndex);
    };

    override onEdit = (e: any) => {
        this.vendorId = e.row.data.id;
        this.isVendorPopupOpened = true;
    };

     get canManage() {
        return this.authService.checkPermission(PermissionType.Vendors, AccessType.Manage);
    }

    get canEdit() {
        return this.authService.checkPermission(PermissionType.Vendors, AccessType.Edit);
    }

    sitesCellValue = (row: VendorGridViewDto) => row.sites?.join(', ') ?? '';
}
